import React, { useEffect, useRef, JSX, useState } from 'react';
import { colors, fonts } from '../../Styles';
import { createPopper, Placement } from '@popperjs/core';
import { DataValue } from '../../Definitions';
import { createPortal } from 'react-dom';

interface popoverProps {
  content: DataValue['value']
  popoverHeader: string
  popoverContent: JSX.Element
  isHovered?: boolean
  referenceRef?: React.RefObject<HTMLDivElement | null>
  placement?: Placement
  overrides?: {
    primaryColor: colors
  }
}
/**
 * PrimaryPopover
 * @param content The element or text to trigger the popover on when clicked.
 * @param popoverHeader The title displayed in the header of the popover.
 * @param popoverContent The JSX content to render inside the popover body.
 * @param overrides Optional styles to customize the popover background color and container styling.
 * @returns A clickable element that shows a styled popover with a header and content when toggled.
 */
export default function SecondaryPopover({content, popoverHeader, popoverContent, isHovered = false, referenceRef, placement, overrides}:popoverProps) {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);
  const [selfHovered, setSelfHovered] = useState<boolean>(isHovered)
  const popoverId = `popover-${Math.random().toString(36).substr(2, 9)}`;

  const _PrimaryColor = overrides&&overrides.primaryColor ? overrides.primaryColor : colors.blueDarkPrimary

  useEffect(() => {
    const reference = referenceRef?.current ? referenceRef.current : internalRef.current
    if ((isHovered||selfHovered) && reference && popperRef.current && arrowRef.current) {
      createPopper(reference, popperRef.current, {
        placement: placement ? placement : "top",
        strategy: 'fixed',
        modifiers: [
          { name: "offset", options: { offset: [0, 14] }},
          { name: "preventOverflow", options: { boundary: document.body }},
          { name: "arrow", options: { element: arrowRef.current }}
        ],
      });
    }
  }, [isHovered, placement, referenceRef, selfHovered]);

  // #region | Popper arrow style and placement
  const popperPlacement = (`
    .${popoverId} [data-popper-arrow] {
      position: absolute;
      width: 25px;
      height: 12px;
      z-index: -1;
      pointer-events: none;
    }
    .${popoverId} [data-popper-arrow]::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: ${_PrimaryColor};
      clip-path: polygon(50% 0%, 0% 100%, 100% 100%); 
      boxShadow: "1px 1px 4px #00000060";
      transform-origin: center;
    }
    .${popoverId}[data-popper-placement^='top'] [data-popper-arrow] {
      bottom: -12px;
    }
    .${popoverId}[data-popper-placement^='top'] [data-popper-arrow]::before {
      transform: rotate(180deg);
    }

    .${popoverId}[data-popper-placement^='bottom'] [data-popper-arrow] {
      top: -12px;
    }
    .${popoverId}[data-popper-placement^='bottom'] [data-popper-arrow]::before {
      transform: rotate(0deg);
    }

    .${popoverId}[data-popper-placement^='left'] [data-popper-arrow] {
      right: -12px;
    }
    .${popoverId}[data-popper-placement^='left'] [data-popper-arrow]::before {
      transform: rotate(90deg);
    }

    .${popoverId}[data-popper-placement^='right'] [data-popper-arrow] {
      left: -12px;
    }
    .${popoverId}[data-popper-placement^='right'] [data-popper-arrow]::before {
      transform: rotate(-90deg);
    }
  `)
  // #endregion

  return ( <>
    <div ref={internalRef} onMouseEnter={() => setSelfHovered(true)} onMouseLeave={() => setSelfHovered(false)}>{content}</div>
    {createPortal(<>
      <style>{popperPlacement}</style>
      <div ref={popperRef} className={popoverId} style={{
        width: 'fit-content',
        height: 'fit-content',
        backgroundColor: colors.greyDarkPrimary,
        zIndex: "9999999999",
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        opacity: isHovered||selfHovered ? 1 : 0,
        visibility: isHovered||selfHovered ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        pointerEvents: isHovered||selfHovered ? 'auto' : 'none',
        position: "relative",
        border: "3px solid " + _PrimaryColor,
        overflow: "visible",
      }}>
        {popoverHeader !== "" && <div style={{width: "100%", height: "20px", backgroundColor: _PrimaryColor, display: "flex", alignItems: "center", justifyContent: "center", ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{popoverHeader}</div>}
        <div style={{ padding: "10px" }}>{popoverContent}</div>
        <div ref={arrowRef} data-popper-arrow/>
      </div> </>, document.body)
    }
  </> );
}